/**
 * Extracteur de données depuis les factures PDF (client-side)
 * Supporte différents fournisseurs: GACD, Megadental, Henry Schein
 * 
 * Livrable pour extraction côté client avec pdfjs-dist + tesseract.js (OCR fallback)
 * Aucune requête réseau, extraction pure côté client
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';

dayjs.extend(customParseFormat);

function isDecimalToken(token) {
  if (!token) return false;
  const normalized = token.replace(/\s/g, '');
  return /\d/.test(normalized) && /[.,]/.test(normalized);
}

function isQuantityValue(token) {
  if (!token) return false;
  return /^\d+[.,]\d+$/.test(token.trim());
}

/**
 * Normalise une chaîne (Unicode, NBSP, espaces)
 */
function normalizeString(s) {
  if (s === null || s === undefined) return '';
  return (s || '')
    .normalize('NFKC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse un montant avec support multi-formats
 */
function parseAmount(s) {
  if (s === null || s === undefined) return null;
  s = String(s).replace(/\u00A0/g, ' ').trim();
  // Remove currency words but keep % for discount extraction outside
  s = s.replace(/€/g, '').replace(/EUR/ig, '').replace(/[^\d.,-]/g, '').trim();
  if (!s) return null;
  
  if (s.indexOf(',') > -1 && s.indexOf('.') > -1) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else {
    s = s.replace(/,/g, '.');
  }
  
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/**
 * Repère un pourcentage dans une ligne texte (55 ou 55,84)
 */
function extractDiscountFromLine(line) {
  if (!line) return null;
  const match = line.match(/(\d{1,3}(?:[.,]\d{1,2})?)\s*%/);
  if (match && match[1]) {
    return parseAmount(match[1]);
  }
  return null;
}

/**
 * Détection robuste du numéro de facture GACD
 */
function detectInvoiceNumber(text) {
  if (!text) return null;

  const explicit = text.match(/facture\s*(?:n[°º]\s*)?[:\s]*\s*(\d{6,12})/i);
  if (explicit && explicit[1]) return explicit[1];

  const docOrigin = text.match(/document\s+origine\s*[:\s]*\s*(\d{6,12})/i);
  if (docOrigin && docOrigin[1]) return docOrigin[1];

  const candidates = [...text.matchAll(/\b(\d{6,12})\b/g)].map(match => ({ num: match[1], idx: match.index ?? 0 }));
  if (candidates.length === 0) return null;

  const factureIdx = text.search(/facture/i);
  if (factureIdx === -1) {
    const preferred = candidates.find(c => c.num.length >= 8) || candidates[0];
    return preferred ? preferred.num : null;
  }

  const after = candidates.filter(c => c.idx >= factureIdx).sort((a, b) => a.idx - b.idx);
  if (after.length > 0) return after[0].num;

  candidates.sort((a, b) => Math.abs(a.idx - factureIdx) - Math.abs(b.idx - factureIdx));
  return candidates[0]?.num || null;
}

/**
 * Calcule le prix net TTC après remise
 */
function computePriceAfterDiscount(unitPrice, discountPercent) {
  if (unitPrice == null) return null;
  if (discountPercent == null) return unitPrice;
  const ratio = 1 - discountPercent / 100;
  return Math.round(unitPrice * ratio * 100) / 100;
}

/**
 * Parser fallback ultra permissif pour une ligne textuelle GACD
 */
function parseLineFallback(line) {
  if (!line || line.length < 4) return null;
  const normalizedLine = normalizeString(line);
  const base = {
    reference: null,
    name: null,
    quantity: null,
    unit_price_ttc_reference: null,
    discount_percent: null,
    unit_price_ttc: null
  };

  const strictMatch = normalizedLine.match(/\b([A-Z0-9]{1,4}-\d{2,6}[A-Z0-9]*)\b\s+(.+?)\s+(\d{1,4})\s+([\d.,]+)\s+(\d{1,3}(?:[.,]\d{1,2})?)\s*%?\s+([\d.,]+)/);
  if (strictMatch) {
    base.reference = strictMatch[1];
    base.name = normalizeString(strictMatch[2]);
    base.name = sanitizeName(base.name.split(' '));
    if (!base.name) return null;
    base.quantity = parseInt(strictMatch[3], 10) || 1;
    base.unit_price_ttc_reference = parseAmount(strictMatch[4]);
    base.discount_percent = parseAmount(strictMatch[5]);
    base.unit_price_ttc = parseAmount(strictMatch[6]);
    if (base.unit_price_ttc == null && base.unit_price_ttc_reference != null && base.discount_percent != null) {
      base.unit_price_ttc = computePriceAfterDiscount(base.unit_price_ttc_reference, base.discount_percent);
    }
    return base;
  }

  const looseMatch = normalizedLine.match(/\b([A-Z0-9]{1,4}-\d{2,6}[A-Z0-9]*)\b\s+(.+?)\s+(\d{1,4})\s+([\d.,]+)(?:\s+(\d{1,3}(?:[.,]\d{1,2})?)\s*%)?(?:\s+([\d.,]+))?/);
  if (looseMatch) {
    base.reference = looseMatch[1];
    base.name = normalizeString(looseMatch[2]);
    base.name = sanitizeName(base.name.split(' '));
    if (!base.name) return null;
    base.quantity = parseInt(looseMatch[3], 10) || 1;
    base.unit_price_ttc_reference = parseAmount(looseMatch[4]);
    base.discount_percent = looseMatch[5] ? parseAmount(looseMatch[5]) : extractDiscountFromLine(normalizedLine);
    base.unit_price_ttc = looseMatch[6] ? parseAmount(looseMatch[6]) : computePriceAfterDiscount(base.unit_price_ttc_reference, base.discount_percent);
    return base;
  }

  const refMatch = normalizedLine.match(/\b([A-Z0-9]{1,4}-\d{2,6}[A-Z0-9]*)\b/);
  if (!refMatch) return null;

  base.reference = refMatch[1];
  const afterRef = normalizedLine.slice(normalizedLine.indexOf(base.reference) + base.reference.length).trim();
  const qtyMatch = afterRef.match(/\b(\d{1,4})\b/);
  if (qtyMatch) base.quantity = parseInt(qtyMatch[1], 10);
  base.discount_percent = extractDiscountFromLine(afterRef);

  const decimalTokens = [...afterRef.matchAll(/(\d{1,3}(?:[.,]\d{1,2})?)/g)].map(m => m[1]);
  if (decimalTokens.length) {
    base.unit_price_ttc = parseAmount(decimalTokens[decimalTokens.length - 1]);
    if (decimalTokens.length >= 2) {
      base.unit_price_ttc_reference = parseAmount(decimalTokens[decimalTokens.length - 2]);
    }
    if (base.unit_price_ttc == null && base.unit_price_ttc_reference != null && base.discount_percent != null) {
      base.unit_price_ttc = computePriceAfterDiscount(base.unit_price_ttc_reference, base.discount_percent);
    }
  }

  const nameCandidate = afterRef.split(/\s+\d{1,4}[^\d]/)[0] || afterRef;
  base.name = sanitizeName(normalizeString(nameCandidate).split(' '));
  if (!base.name) return null;
  return base;
}

/**
 * Parse une date vers ISO YYYY-MM-DD
 */
function toISODate(s) {
  if (!s) return null;
  s = normalizeString(s).replace(/\s*([/.-])\s*/g, '$1');
  
  // Formats supportés
  const formats = ['DD.MM.YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'DD.MM.YY'];
  let parsed = null;
  
  for (const fmt of formats) {
    parsed = dayjs(s, fmt, true);
    if (parsed.isValid()) break;
  }
  
  return parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * Détecte le fournisseur depuis le texte
 */
function detectSupplier(text, pageCount) {
  const upperText = text.toUpperCase();
  const topPortion = text.substring(0, Math.floor(text.length * 0.4)); // top 40%
  const topUpper = topPortion.toUpperCase();
  
  if (topUpper.includes('GACD') || topUpper.includes('GACD SAS') || topUpper.includes('VOTRE METIER, NOTRE COMBAT')) {
    return 'GACD';
  }
  const megaMarkers = ['MEGADENTAL', 'MEGA DENTAL', 'WWW.MEGADENTAL.FR', 'RUE DES INVESTISSEURS', 'INVESTISSEURS . ZA DE LA PLAINE'];
  if (megaMarkers.some(marker => topUpper.includes(marker) || upperText.includes(marker))) {
    return 'MEGADENTAL';
  }
  if (topUpper.includes('HENRY-SCHEIN') || topUpper.includes('HENRY SCHEIN')) {
    return 'HENRY-SCHEIN';
  }
  
  return null;
}

function detectGACDLayout(text) {
  const upper = text.toUpperCase();
  if (upper.includes('ENDODONTIE') && upper.includes('HYGIENE')) {
    return 'CATALOGUE';
  }
  if (upper.includes('MA FACTURE DU') && upper.includes('TOTAL TTC')) {
    return 'STANDARD';
  }
  return 'VARIANT';
}

const HEADER_KEYWORDS = ['référence', 'désignation', 'qté', 'quantité', '%', 'remise', 'prix', 'net'];

function isCategoryHeaderLine(text) {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (/[0-9]/.test(trimmed)) return false;
  return /^(ENDODONTIE|HYGIENE|RESTAURATION|PROTHESE|CHIRURGIE|CONSERVATION|RADIOLOGIE|MATERIEL|INSTRUMENTATION|ORTHODONTIE|PREVENTION|STERILISATION)$/i.test(trimmed);
}

function shouldSkipNonItemLine(text) {
  const lower = text.toLowerCase();
  if (!text) return true;
  if (/^commande\b/.test(lower)) return true;
  if (/^livraison\b/.test(lower)) return true;
  if (/^référence externe/i.test(lower)) return true;
  if (/^total/i.test(lower)) return true;
  if (/^net à payer/i.test(lower)) return true;
  if (/^montant tva/i.test(lower)) return true;
  return false;
}

const LEADING_NOISE_WORDS = new Set([
  'de', 'du', 'des', 'd', 'la', 'le', 'les', 'au', 'aux', 'et', 'à', 'en', 'sur', 'par', 'nos', 'vos',
  'notre', 'votre', 'son', 'sa', 'avec', 'pour', 'ou', 'un', 'une', 'facture', 'n°', 'nº', 'page',
  'exemplaire', 'adresse', 'livraison', 'rdc', 'bd', 'selarl', 'meir', 'amzallag', 'tel', 'tél',
  'service', 'technique', 'montant', 'total', 'ht', 'ttc', 'tva', 'numéro', 'client', 'commande'
]);

const TRAILING_NOISE_WORDS = new Set([
  'eur', 'ttc', 'ht', 'net', 'montant', 'total', 'page', 'exemplaire', 'tva', 'eur.', 'france', 'gacd',
  'bp', 'fm', 'jours', 'etat', 'etat.', 'ligne'
]);

const INTERNAL_NOISE_WORDS = new Set([
  'facture', 'montant', 'total', 'base', 'ht', 'ttc', 'tva', 'service', 'technique', 'client',
  'clients', 'livraison', 'commande', 'retour', 'retours', 'conditions', 'generales', 'condition',
  'générales', 'selarl', 'amzallag', 'gacd', 'sas', 'adresse', 'mail', 'mode', 'echeance', 'échéance',
  'motif', 'net', 'payer', 'paiement', 'identification', 'fiscale', 'siren', 'iban', 'swift', 'bp',
  'cedex', 'france', 'tremblay-en-france', 'offerts', 'frais', 'port', 'effectuez', 'question',
  'questions', 'souhaitez', 'appeler', 'suivi', 'infos', 'web', 'coupon', 'reglement', 'règlement',
  'comptabilite', 'comptabilité', 'grandscomptes', 'technique@gacd.fr', 'clients@gacd.fr',
  'comptabilite@gacd.fr', 'gacd.fr', 'jacob.parienti@gmail.com', 'é', 'lection', 'produits',
  'fournitures', 'dentaires', 'moment', 'sans', 'avis', 'message', 'ultérieurement', 'ultérieurement,',
  'ultérieurement.', 'metropolitaine', 'métropolitaine', 'identification', 'fiscale', 'capital',
  'cedex', 'infos...', 'question?', 'france?'
]);

function isNoiseToken(token) {
  const value = token.toLowerCase();
  if (value === '') return true;
  if (/^\d{1,2}h\d{2}$/i.test(token)) return true;
  if (/^[0-9]{1,2}h$/i.test(token)) return true;
  if (/^0{1,2}$/.test(token)) return true;
  return false;
}

function shouldTrimLeading(token) {
  const value = token.toLowerCase();
  if (LEADING_NOISE_WORDS.has(value)) return true;
  if (isNoiseToken(token)) return true;
  if (value.length <= 2 && value === value.toLowerCase()) return true;
  return false;
}

function shouldTrimTrailing(token) {
  const value = token.toLowerCase();
  if (TRAILING_NOISE_WORDS.has(value)) return true;
  if (isNoiseToken(token)) return true;
  if (/^eur$/i.test(token)) return true;
  if (/^€$/.test(token)) return true;
  if (/^page$/i.test(token)) return true;
  if (/^conditions$/i.test(token)) return true;
  if (/^g\.a\.c\.d\.?$/i.test(token)) return true;
  return false;
}

function cleanNameTokens(nameTokens) {
  let start = 0;
  let end = nameTokens.length - 1;
  while (start <= end && shouldTrimLeading(nameTokens[start])) start++;
  while (end >= start && shouldTrimTrailing(nameTokens[end])) end--;
  const slice = nameTokens.slice(start, end + 1);
  return slice.filter(token => {
    const lower = token.toLowerCase();
    if (isNoiseToken(token)) return false;
    if (TRAILING_NOISE_WORDS.has(lower)) return false;
    if (INTERNAL_NOISE_WORDS.has(lower)) return false;
    if (/^\d{5,}$/.test(lower)) return false;
    if (/^\d+[.,]\d{2}$/.test(lower)) return false;
    if (/^www\./i.test(token)) return false;
    if (/@/.test(token)) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailPattern.test(token)) return false;
    }
    if (/^fr\d{2}/i.test(token)) return false;
    if (/^[0-9]{2,}[-/][0-9a-z]+$/i.test(token) && lower.length > 8 && !/[a-z]/i.test(lower.substring(0, 1))) {
      // leave potential references intact
      return true;
    }
    return true;
  });
}

function sanitizeName(nameTokens) {
  if (!nameTokens || nameTokens.length === 0) return '';
  const cleaned = cleanNameTokens(nameTokens);
  if (cleaned.length === 0) return '';
  let result = normalizeString(cleaned.join(' '));

  const removalPatterns = [
    /\bmontant total\b.*$/i,
    /\btotal ttc\b.*$/i,
    /\btotal ht\b.*$/i,
    /\badresse mail\b.*$/i,
    /\bnos conseillers\b.*$/i,
    /\bdu lundi au vendredi\b.*?(?=[A-Z0-9]{2,})/i,
    /\bcommande\s+\d+.*?(?=[A-Z0-9]{2,}-\d{2,})/i,
    /\bgacd\s+sas\b.*$/i,
    /\bnos conditions\b.*$/i,
    /\ba\s+e?s?\s+la date indicative de livraison.*$/i
  ];

  removalPatterns.forEach(pattern => {
    result = result.replace(pattern, '').trim();
  });

  result = result.replace(/^de\s+\d{1,2}h\d{2}\s+/i, '').trim();
  result = result.replace(/à\s+è\s*s?\s+la date indicative de livraison.*$/i, '').trim();
  result = result.replace(/a\s+e?s?\s+la date indicative de livraison.*$/i, '').trim();

  return result.trim();
}

function isNoisyName(name) {
  if (!name) return true;
  const lower = name.toLowerCase();
  return /facture n|gacd|sas|du lundi au vendredi|é lection|lection de produits|montant total|adresse mail|conditions générales|frais de port/.test(lower);
}

function mergeGACDItems(tokenItems = [], textItems = []) {
  const mergedMap = new Map();
  const order = [];

  const upsert = (item, source) => {
    if (!item || !item.reference) return;
    const key = normalizeString(item.reference);
    let existing = mergedMap.get(key);
    if (!existing) {
      if (source === 'tokens') {
        existing = { ...item, _sources: new Set([source]) };
        mergedMap.set(key, existing);
        order.push(key);
      }
      return;
    }

    existing._sources.add(source);

    if (item.name) {
      if (!existing.name || isNoisyName(existing.name)) {
        existing.name = item.name;
      } else if (!isNoisyName(item.name) && item.name.length > existing.name.length) {
        existing.name = item.name;
      }
    }

    if (item.quantity != null) {
      if (existing.quantity == null || (existing.quantity === 1 && item.quantity > 1)) {
        existing.quantity = item.quantity;
      }
    }

    if (item.unit_price_ttc != null) {
      if (existing.unit_price_ttc == null) {
        existing.unit_price_ttc = item.unit_price_ttc;
      }
    }

    if (item.unit_price_ttc_reference != null) {
      if (existing.unit_price_ttc_reference == null) {
        existing.unit_price_ttc_reference = item.unit_price_ttc_reference;
      }
    }

    if (item.discount_percent != null) {
      if (existing.discount_percent == null) {
        existing.discount_percent = item.discount_percent;
      }
    }

    if (item.source_line != null) {
      if (existing.source_line == null || (source === 'tokens' && item.source_line < existing.source_line)) {
        existing.source_line = item.source_line;
      }
    }
  };

  tokenItems.forEach(item => upsert(item, 'tokens'));
  textItems.forEach(item => upsert(item, 'text'));

  return order.map(key => {
    const entry = mergedMap.get(key);
    delete entry._sources;
    return { ...entry };
  });
}

function extractGACDItemsFromTokens(tokens, opts, warnings, layout = 'STANDARD') {
  if (!tokens || tokens.length === 0) return { items: [], rows: [] };

  const tolerance = 2;
  const rowsMap = {};
  for (const token of tokens) {
    const yRounded = Math.round(token.y / tolerance) * tolerance;
    if (!rowsMap[yRounded]) rowsMap[yRounded] = [];
    rowsMap[yRounded].push(token);
  }

  const sortedEntries = Object.entries(rowsMap).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
  const rows = sortedEntries.map(([y, rowTokens], idx) => {
    const sortedTokens = [...rowTokens].sort((a, b) => a.x - b.x);
    return {
      line_no: idx + 1,
      y: parseFloat(y),
      tokens: sortedTokens,
      text: sortedTokens.map(t => t.s).join(' '),
      page: sortedTokens[0]?.page ?? null
    };
  });

  const items = [];
  const headerX = {};
  let headerFound = false;

  for (const row of rows) {
    const rowLower = row.text.toLowerCase();

    if (!headerFound) {
      const matches = HEADER_KEYWORDS.filter(kw => rowLower.includes(kw));
      if (matches.length >= 3) {
      headerFound = true;
        for (const token of row.tokens) {
          const lower = token.s.toLowerCase();
          if (/référence|ref|réf/.test(lower)) headerX.ref = token.x;
          if (/qté|quantité/.test(lower)) headerX.qty = token.x;
          if ((/%|remise/.test(lower)) && headerX.discount === undefined) headerX.discount = token.x;
          if (((/prix/.test(lower) && /net/.test(lower)) || /pu/.test(lower)) && headerX.price === undefined) headerX.price = token.x;
        }
        if (opts.debug) console.debug('[GACD DEBUG] Header detected at line', row.line_no, headerX);
      }
              continue;
            }
            
    if (isCategoryHeaderLine(row.text)) {
      if (opts.debug) console.debug('[GACD ⚠️] Skip category row:', row.line_no, row.text.trim());
        continue;
      }
      
    if (/^total|net à payer|montant tva/i.test(rowLower)) {
      if (opts.debug) console.debug('[GACD DEBUG] Totals row detected (ignored):', row.line_no);
      continue;
    }

    const refToken = row.tokens.find(t => /[A-Z0-9]{1,4}-\d{2,6}[A-Z0-9]*/.test(t.s));
    if (!refToken) continue;
    const reference = normalizeString(refToken.s);
    if (/^200-?000$/i.test(reference)) {
      if (opts.debug) console.debug('[GACD ⚠️] Skip shipping row:', row.line_no, reference);
              continue;
            }
            
    const sortedRow = row.tokens.filter(t => t.x >= refToken.x - 5);
    let refSeen = false;
          let nameEnded = false;
    const nameTokens = [];
    let quantity = null;
    let discount = null;
    let discountX = null;
    const decimalTokens = [];

    const qtyX = headerX.qty;
    const discountXHeader = headerX.discount;
    const priceX = headerX.price;
    const designationMaxX = (() => {
      const boundaries = [qtyX, discountXHeader, priceX].filter(x => Number.isFinite(x));
      if (boundaries.length === 0) return refToken.x + 350;
      return Math.min(...boundaries) - 10;
    })();

    const isQuantityCandidate = (token, idx) => {
      if (!/^\d{1,4}$/.test(token.s)) return false;
                const num = parseInt(token.s, 10);
      if (num < 1 || num > 999) return false;
      const prev = sortedRow[idx - 1];
      const prevHasAlpha = prev ? /[A-Za-z]/.test(prev.s) : false;
      const nextSlice = sortedRow.slice(idx + 1, idx + 4);
      const hasFollowingValues = nextSlice.some(t => isDecimalToken(t.s) || /%/.test(t.s));
      if (!hasFollowingValues) return false;
      if (!prevHasAlpha && layout === 'STANDARD') return false;
      return true;
    };

    const qtyTolerance = 40;
    const discountTolerance = 45;
    const priceTolerance = 70;

    sortedRow.forEach((token, idx) => {
      if (!refSeen) {
        if (token === refToken) refSeen = true;
        return;
      }

      const value = token.s;
      const decimal = isDecimalToken(value);
      const percent = /%/.test(value);
      const nearQty = Number.isFinite(qtyX) && Math.abs(token.x - qtyX) <= qtyTolerance;
      const nearDiscount = Number.isFinite(discountXHeader) && Math.abs(token.x - discountXHeader) <= discountTolerance;
      const nearPrice = Number.isFinite(priceX) && Math.abs(token.x - priceX) <= priceTolerance;
      const beyondDesignation = token.x > designationMaxX + 5;

      const numericCandidate = /^\d{1,4}$/.test(value);
      const considerQuantity = quantity == null && (nearQty || (!Number.isFinite(qtyX) && numericCandidate && isQuantityCandidate(token, idx)));

      if (considerQuantity && numericCandidate) {
        if (opts.debug) console.debug('[GACD DEBUG] Quantity candidate line', row.line_no, 'value', value, 'tokenX', token.x, 'qtyX', qtyX);
        quantity = parseInt(value, 10);
                    nameEnded = true;
        if (opts.debug) console.debug('[GACD DEBUG] Quantity detected line', row.line_no, quantity);
        return;
      }

      if (quantity == null && isQuantityCandidate(token, idx)) {
        quantity = parseInt(value, 10);
        nameEnded = true;
        if (opts.debug) console.debug('[GACD DEBUG] Quantity detected line', row.line_no, quantity);
        return;
      }

      if (percent && discount == null && (nearDiscount || (quantity != null && (!Number.isFinite(priceX) || token.x <= priceX + 10)))) {
        const parsed = extractDiscountFromLine(value);
        if (parsed != null) {
          discount = parsed;
          discountX = token.x;
          nameEnded = true;
          return;
        }
      }

      if (decimal && (nearPrice || nearDiscount || beyondDesignation || quantity != null)) {
        const amount = parseAmount(value);
        if (amount != null) {
          decimalTokens.push({ value: amount, x: token.x });
          nameEnded = true;
          return;
        }
      }

      if (!nameEnded) {
        if (beyondDesignation) {
          nameEnded = true;
        } else {
          nameTokens.push(value);
        }
      } else if (!decimal && !percent && !numericCandidate && !beyondDesignation) {
        nameTokens.push(value);
      }
    });

    if (discount == null) {
      const percentTokenNearColumn = row.tokens.find(t => /%/.test(t.s) && (
        (Number.isFinite(discountXHeader) && Math.abs(t.x - discountXHeader) <= discountTolerance) ||
        (Number.isFinite(qtyX) && t.x > qtyX + 40)
      ));
      if (percentTokenNearColumn) {
        const recovered = extractDiscountFromLine(row.text);
        if (recovered != null) {
          discount = recovered;
        }
      }
    }

    const sanitizedName = sanitizeName(nameTokens);
    if (!sanitizedName) continue;

    decimalTokens.sort((a, b) => a.x - b.x);
    if (opts.debug && opts.inspectDecimals && Array.isArray(opts.inspectDecimals) && opts.inspectDecimals.includes(row.line_no)) {
      console.debug('[GACD DEBUG] Decimals row', row.line_no, decimalTokens);
    }
    let unitPriceRef = null;
    let unitPriceNet = null;
    let netLineTotal = null;
    let netCandidateX = null;
    let totalCandidateX = null;

    if (decimalTokens.length === 1) {
      unitPriceNet = decimalTokens[0].value;
    } else if (decimalTokens.length >= 2) {
      let netCandidate = null;
      if (quantity != null) {
        let bestScore = Number.POSITIVE_INFINITY;
        decimalTokens.forEach(candidate => {
          const expectedTotal = Math.round(candidate.value * quantity * 100) / 100;
          const match = decimalTokens.find(other => other !== candidate && Math.abs(other.value - expectedTotal) < 0.4);
          if (match) {
            const score = Math.abs(match.x - candidate.x);
            if (score < bestScore) {
              bestScore = score;
              netCandidate = candidate;
              netLineTotal = match.value;
              netCandidateX = candidate.x;
              totalCandidateX = match.x;
            }
          }
        });
      }

      if (!netCandidate) {
        const afterDiscount = discountX != null
          ? decimalTokens.filter(d => d.x >= discountX - 5)
          : decimalTokens;
        netCandidate = afterDiscount.length > 0 ? afterDiscount[0] : decimalTokens[0];
        netCandidateX = netCandidate?.x ?? null;
      }

      unitPriceNet = netCandidate?.value ?? null;
      if (netLineTotal != null && quantity != null && quantity > 0) {
        unitPriceNet = Math.round((netLineTotal / quantity) * 100) / 100;
      }

      const refCandidates = decimalTokens.filter(d => {
        if (!netCandidate) return true;
        if (Math.abs(d.value - unitPriceNet) < 0.01) return false;
        return d.x <= (netCandidate.x + 1);
      });

      if (refCandidates.length > 0) {
        unitPriceRef = refCandidates[refCandidates.length - 1].value;
      }
    }

    if (netLineTotal == null && Number.isFinite(priceX)) {
      const totalsAfterPrice = decimalTokens.filter(d => d.x > priceX + 20 && d.x < priceX + 140);
      if (totalsAfterPrice.length > 0) {
        let chosenTotal = totalsAfterPrice[0];
        const targetTotalX = headerX.totalTtc ?? (priceX != null ? priceX + 45 : null);
        if (targetTotalX != null) {
          chosenTotal = totalsAfterPrice.reduce((best, candidate) => {
            const bestDiff = Math.abs(best.x - targetTotalX);
            const candDiff = Math.abs(candidate.x - targetTotalX);
            return candDiff < bestDiff ? candidate : best;
          }, chosenTotal);
        }
        if (unitPriceNet != null && quantity != null) {
          const expected = unitPriceNet * quantity;
          chosenTotal = totalsAfterPrice.reduce((best, candidate) => {
            const bestDiff = Math.abs(best.value - expected);
            const candDiff = Math.abs(candidate.value - expected);
            return candDiff < bestDiff ? candidate : best;
          }, chosenTotal);
        }
        netLineTotal = chosenTotal.value;
        if (quantity != null && quantity > 0) {
          unitPriceNet = Math.round((netLineTotal / quantity) * 100) / 100;
        }
      }
    }

    if (headerX.price === undefined && netCandidateX != null) {
      headerX.price = netCandidateX;
    }

    if (headerX.totalTtc === undefined && totalCandidateX != null) {
      headerX.totalTtc = totalCandidateX;
    }

    if (unitPriceNet == null && unitPriceRef != null && discount != null) {
      unitPriceNet = computePriceAfterDiscount(unitPriceRef, discount);
    }
    if (unitPriceRef == null && unitPriceNet != null && discount != null) {
      const inverse = unitPriceNet / (1 - discount / 100);
      if (Number.isFinite(inverse)) unitPriceRef = Math.round(inverse * 100) / 100;
    }

    if (unitPriceRef == null && unitPriceNet != null) {
      unitPriceRef = unitPriceNet;
    }

    if (unitPriceNet == null && unitPriceRef == null) {
          continue;
        }
        
    const item = {
      reference,
      name: sanitizedName,
      quantity: quantity || 1,
      unit_price_ttc: unitPriceNet,
      unit_price_ttc_reference: unitPriceRef,
      discount_percent: discount,
      source_line: row.line_no,
      line_total_ttc: netLineTotal ?? (unitPriceNet != null && quantity != null ? Math.round(unitPriceNet * quantity * 100) / 100 : null)
    };

    if (opts.debug && opts.inspectDecimals && Array.isArray(opts.inspectDecimals) && opts.inspectDecimals.includes(row.line_no)) {
      console.debug('[GACD DEBUG] Line total row', row.line_no, item.line_total_ttc);
    }

    items.push(item);
    if (opts.debug) {
      console.debug('[GACD ✅] Article ligne', row.line_no, ':', item.reference, item.name, 'q=', item.quantity, 'ref=', item.unit_price_ttc_reference, 'net=', item.unit_price_ttc, 'rem=', item.discount_percent);
    }
  }

  return { items, rows };
}

function extractGACDItemsFromText(text, layout, opts, warnings) {
  const lines = text.split('\n');
  const rows = [];
  const items = [];
  let headerFound = false;

  for (const rawLine of lines) {
    const normalized = normalizeString(rawLine);
    if (!normalized) continue;
    const row = {
      line_no: rows.length + 1,
      text: normalized
    };
    rows.push(row);

    const lower = normalized.toLowerCase();
    if (!headerFound) {
      const matches = HEADER_KEYWORDS.filter(kw => lower.includes(kw));
      if (matches.length >= 3 || /\b[A-Z0-9]{1,4}-\d{2,6}[A-Z0-9]*\b/.test(normalized)) {
        headerFound = true;
      }
      continue;
    }

    if (isCategoryHeaderLine(normalized)) continue;
    if (shouldSkipNonItemLine(normalized)) continue;
    if (/^total|net à payer|montant tva/i.test(lower)) continue;

    const parsed = parseLineFallback(normalized);
    if (!parsed || !parsed.reference || !parsed.name) continue;
    if (/^200-?000$/i.test(parsed.reference)) continue;
    if (parsed.unit_price_ttc == null && parsed.unit_price_ttc_reference == null) continue;

    const discount = parsed.discount_percent != null ? Number(parsed.discount_percent) : null;
    const unitRef = parsed.unit_price_ttc_reference != null ? parsed.unit_price_ttc_reference : null;
    const unitNet = parsed.unit_price_ttc != null ? parsed.unit_price_ttc : computePriceAfterDiscount(unitRef, discount);

    items.push({
      reference: normalizeString(parsed.reference),
      name: parsed.name,
      quantity: parsed.quantity || 1,
      unit_price_ttc_reference: unitRef,
      unit_price_ttc: unitNet,
      discount_percent: discount,
      source_line: row.line_no
    });
  }

  return { items, rows };
}

/**
 * Extraire données facture GACD avec parsing avancé
 */
function extractGACD(text, opts = {}, tokens = []) {
  const warnings = [];
  const layout = detectGACDLayout(text);
  const result = {
    supplier: 'GACD',
    invoice_number: detectInvoiceNumber(text),
    client_number: null,
    invoice_date: null,
    total_ttc: null,
    items: [],
    raw_text: opts.debug ? text : null,
    warnings: []
  };

  if (!result.invoice_number) warnings.push('Numéro de facture non trouvé');

  const clientExplicit = text.match(/n[°º]\s*de\s*(?:client|compte)\s*[:\s]*\s*(\d{6,12})\b/i);
  if (clientExplicit && clientExplicit[1]) {
    result.client_number = clientExplicit[1];
  } else {
    const clientNearby = text.match(/\bclient\b[^\d]{0,30}(\d{6,12})/i);
    if (clientNearby && clientNearby[1]) result.client_number = clientNearby[1];
  }
  if (!result.client_number) warnings.push('Numéro client non trouvé');

  const textLower = normalizeString(text).toLowerCase();
  let invoiceDate = null;
  const dateKeywords = ['date de la facture', 'date facture', 'ma facture du', 'facture du'];
  for (const keyword of dateKeywords) {
    const idx = textLower.indexOf(keyword);
    if (idx !== -1) {
      const search = textLower.substring(idx, Math.min(idx + keyword.length + 150, textLower.length));
      const match = search.match(/(\d{2}[./-]\d{2}[./-]\d{4})/);
      if (match && match[1]) {
        invoiceDate = toISODate(match[1]);
        if (invoiceDate) break;
      }
    }
  }
  if (!invoiceDate) {
    const patterns = [
      /date\s+de\s+la\s+facture\s*:??\s*(\d{2}[./-]\d{2}[./-]\d{4})/i,
      /date\s+facture\s*:??\s*(\d{2}[./-]\d{2}[./-]\d{4})/i,
      /ma\s+facture\s+du\s+(\d{2}[./-]\d{2}[./-]\d{4})/i,
      /facture\s+du\s+(\d{2}[./-]\d{2}[./-]\d{4})/i
    ];
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        invoiceDate = toISODate(match[1]);
        if (invoiceDate) break;
      }
    }
  }
  if (!invoiceDate) {
  const factureIdx = textLower.indexOf('facture');
    if (factureIdx !== -1) {
      const search = textLower.substring(factureIdx, Math.min(factureIdx + 300, textLower.length));
      if (!/(échéance|payer avant|due date)/.test(search)) {
        const match = search.match(/(\d{2}[./-]\d{2}[./-]\d{4})/);
        if (match && match[1]) invoiceDate = toISODate(match[1]);
      }
    }
  }
  if (invoiceDate) {
    result.invoice_date = invoiceDate;
  } else {
    warnings.push('Date de facture non trouvée');
  }

  const totalMatch = text.match(/(?:total ttc|montant ttc|net ?a ?payer|net à payer|net a payer|net a régler)\b[\s:-]*([0-9\s\u00A0.,]+)\s*(€|EUR)?/i);
  if (totalMatch && totalMatch[1]) {
    result.total_ttc = parseAmount(totalMatch[1]);
  }
  if (result.total_ttc == null) warnings.push('Total TTC non trouvé');

  const tokenData = tokens && tokens.length
    ? extractGACDItemsFromTokens(tokens, opts, warnings, layout)
    : { items: [], rows: [] };
  const textData = extractGACDItemsFromText(text, layout, opts, warnings);
      
      if (opts.debug) {
    console.debug('[GACD DEBUG] Token items count:', tokenData.items.length);
    console.debug('[GACD DEBUG] Text fallback items count:', textData.items.length);
  }

  const mergedItems = mergeGACDItems(tokenData.items, textData.items);

  result.items = mergedItems.map(item => {
    const clean = { ...item };
    if (clean.unit_price_ttc == null && clean.unit_price_ttc_reference != null && clean.discount_percent != null) {
      clean.unit_price_ttc = computePriceAfterDiscount(clean.unit_price_ttc_reference, clean.discount_percent);
    }
    if (clean.discount_percent != null) clean.discount_percent = Number(clean.discount_percent);
    return clean;
  });

  result.items = result.items.map(item => {
    const clean = { ...item };
    if (clean.unit_price_ttc != null && clean.discount_percent != null) {
      const expectedRef = clean.discount_percent >= 100
        ? clean.unit_price_ttc
        : Math.round((clean.unit_price_ttc / (1 - clean.discount_percent / 100)) * 100) / 100;
      if (!clean.unit_price_ttc_reference || Math.abs(clean.unit_price_ttc_reference - expectedRef) > 0.5) {
        clean.unit_price_ttc_reference = expectedRef;
      }
    }
    if (clean.unit_price_ttc == null && clean.unit_price_ttc_reference != null && clean.discount_percent != null) {
      clean.unit_price_ttc = computePriceAfterDiscount(clean.unit_price_ttc_reference, clean.discount_percent);
    }
    if (clean.line_total_ttc == null && clean.unit_price_ttc != null && clean.quantity != null) {
      clean.line_total_ttc = Math.round(clean.unit_price_ttc * clean.quantity * 100) / 100;
    }
    return clean;
  });

  const itemsTotal = result.items.reduce((acc, item) => {
    const qty = item.quantity || 1;
    const unit = item.unit_price_ttc != null ? item.unit_price_ttc : computePriceAfterDiscount(item.unit_price_ttc_reference, item.discount_percent) || 0;
    const lineTotal = item.line_total_ttc != null ? item.line_total_ttc : qty * unit;
    return Math.round((acc + lineTotal) * 100) / 100;
  }, 0);

  if (result.total_ttc != null && Math.abs(itemsTotal - result.total_ttc) > 1) {
    warnings.push(`Somme des lignes (${itemsTotal} €) différente du total TTC (${result.total_ttc} €)`);
  }

  if (opts.debug) {
    const debugRows = textData.rows.length ? textData.rows : tokenData.rows;
    result.debug_lines = debugRows.map(row => `${row.line_no}. ${row.text}`);
  }

  result.warnings = warnings;
  return result;
}

function sanitizeMegadentalReference(ref) {
  return normalizeString(ref).replace(/[–—]/g, '-');
}

function extractMegaDentalItemsFromTokens(pageTokens, opts = {}, pageNum = null) {
  if (!pageTokens || pageTokens.length === 0) {
    return { items: [], headers: new Map() };
  }

  const tolerance = 2;
  const rowsMap = {};

  for (const token of pageTokens) {
    const yRounded = Math.round(token.y / tolerance) * tolerance;
    if (!rowsMap[yRounded]) rowsMap[yRounded] = [];
    rowsMap[yRounded].push(token);
  }

  const rowEntries = Object.entries(rowsMap).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
  const items = [];
  const headers = new Map();
  const invoiceNumberToKey = new Map();
  let autoCounter = 0;
  let currentKey = null;

  const ensureHeader = ({ invoiceNumber, fallbackKey }) => {
    let key = null;
    const normalizedInvoice = invoiceNumber ? normalizeString(invoiceNumber) : null;
    if (normalizedInvoice) {
      key = invoiceNumberToKey.get(normalizedInvoice);
      if (!key) {
        key = `inv-${normalizedInvoice}`;
        invoiceNumberToKey.set(normalizedInvoice, key);
      }
    } else {
      key = fallbackKey || `auto-${pageNum ?? 0}-${++autoCounter}`;
    }

    let header = headers.get(key);
    if (!header) {
      header = {
        invoice_number: normalizedInvoice || null,
        client_number: null,
        order_number: null,
        invoice_date: null,
        total_declared: null,
        pages: new Set()
      };
      headers.set(key, header);
    } else if (normalizedInvoice && !header.invoice_number) {
      header.invoice_number = normalizedInvoice;
    }

    if (pageNum != null) header.pages.add(pageNum);
    return { key, header };
  };

  const assignMetadata = (key, mutator) => {
    if (!key) return;
    const header = headers.get(key);
    if (!header) return;
    mutator(header);
  };

  for (const [yKey, rowTokens] of rowEntries) {
    const sorted = rowTokens
      .filter(t => t.s && t.s.length > 0)
      .sort((a, b) => a.x - b.x);

    const rawRowTokens = sorted.map(t => t.s.trim()).filter(Boolean);
    const rawRowText = rawRowTokens.join(' ');
    const rawUpper = rawRowText.toUpperCase();

    if (rawRowTokens.length) {
      if (rawRowText.startsWith('#1#')) {
        const parts = rawRowText.split('#');
        const clientNumber = normalizeString(parts[2] || '');
        const invoiceNumber = normalizeString(parts[3] || '');
        const invoiceDate = toISODate(parts[4] || '');
        const orderNumber = normalizeString(parts[5] || '');
        const totalDeclared = parseAmount(parts[7] || '');
        const fallbackKey = `hash-${pageNum ?? 0}-${++autoCounter}`;
        const { key, header } = ensureHeader({ invoiceNumber, fallbackKey });
        if (clientNumber) header.client_number = header.client_number || clientNumber;
        if (orderNumber) header.order_number = header.order_number || orderNumber;
        if (invoiceDate) header.invoice_date = header.invoice_date || invoiceDate;
        if (totalDeclared != null) header.total_declared = totalDeclared;
        currentKey = key;
        continue;
      }

      const invoiceMatch = rawRowText.match(/N[°º]\s*FACTURE\s+([0-9]+)/i);
      if (invoiceMatch) {
        const { key } = ensureHeader({ invoiceNumber: invoiceMatch[1] });
        currentKey = key;
        continue;
      }

      const clientMatch = rawRowText.match(/N[°º]\s*CLIENT\s+([0-9]+)/i);
      if (clientMatch) {
        const clientNumber = normalizeString(clientMatch[1]);
        if (currentKey) {
          assignMetadata(currentKey, header => {
            if (clientNumber) header.client_number = header.client_number || clientNumber;
          });
        } else {
          const { key } = ensureHeader({ invoiceNumber: null, fallbackKey: `client-${pageNum ?? 0}-${++autoCounter}` });
          currentKey = key;
          assignMetadata(key, header => {
            if (clientNumber) header.client_number = header.client_number || clientNumber;
          });
        }
        continue;
      }

      const orderMatch = rawRowText.match(/N[°º]\s*COMMANDE\s+([0-9]+)/i);
      if (orderMatch && currentKey) {
        const orderNumber = normalizeString(orderMatch[1]);
        assignMetadata(currentKey, header => {
          if (orderNumber) header.order_number = header.order_number || orderNumber;
        });
        continue;
      }

      const dateMatch = rawRowText.match(/DATE\s+(?:FACTURE\s+)?(\d{2}\s*[\/\.\-]\s*\d{2}\s*[\/\.\-]\s*\d{4})/i);
      if (dateMatch) {
        const invoiceDate = toISODate(dateMatch[1]);
        if (currentKey) {
          assignMetadata(currentKey, header => {
            if (invoiceDate) header.invoice_date = header.invoice_date || invoiceDate;
          });
        } else {
          const { key } = ensureHeader({ invoiceNumber: null, fallbackKey: `date-${pageNum ?? 0}-${++autoCounter}` });
          currentKey = key;
          assignMetadata(key, header => {
            if (invoiceDate) header.invoice_date = header.invoice_date || invoiceDate;
          });
        }
        continue;
      }
    }

    const lineTokens = sorted
      .map(t => ({ value: t.s.trim(), x: t.x }))
      .filter(t => t.value.length > 0);

    if (lineTokens.length < 5) continue;

    const rowText = lineTokens.map(t => t.value).join(' ');
    const joinedUpper = rowText.toUpperCase();
    if (/^CODE/.test(joinedUpper) || /^DESIGNATION/.test(joinedUpper) || /^BASE H\.T/.test(joinedUpper) || /^CONDITION DE REGLEMENT/.test(joinedUpper) || /^TOTAL TVA/.test(joinedUpper) || /^NET A PAYER/.test(joinedUpper) || /^SUITE AU VERSO/.test(joinedUpper) || /^IMPORTANT/.test(joinedUpper)) {
      if (/NET\s+A\s+PAYER/i.test(joinedUpper)) {
        const amountMatch = rowText.match(/NET\s+A\s+PAYER\s+([0-9\s.,]+)/i);
        if (amountMatch) {
          const amount = parseAmount(amountMatch[1]);
          if (amount != null && currentKey) {
            assignMetadata(currentKey, header => { header.total_declared = amount; });
          }
        }
      }
      continue;
    }

    const referenceRaw = lineTokens[0].value;
    const reference = sanitizeMegadentalReference(referenceRaw);
    if (!reference || reference.length < 3 || !/[0-9A-Z]/i.test(reference)) continue;

    const metricsStartX = 320;
    const firstMetricsIdx = lineTokens.findIndex((token, index) => index > 0 && token.x >= metricsStartX && (isQuantityValue(token.value) || /%/.test(token.value)));
    const cutoffIdx = firstMetricsIdx === -1 ? lineTokens.length : firstMetricsIdx;
    const nameTokens = lineTokens.slice(1, cutoffIdx).map(t => t.value);
    if (nameTokens.length === 0) continue;

    const rawName = sanitizeName(nameTokens);
    if (!rawName) {
      if (opts.debug) {
        console.debug('[MEGA DEBUG] Skip row (empty name)', reference, nameTokens);
      }
      continue;
    }

    const detailTokens = lineTokens.slice(cutoffIdx);
    if (detailTokens.length < 3) continue;

    let detailIndex = 0;

    let quantityOrdered = null;
    let quantityDelivered = null;
    const firstDetail = detailTokens[detailIndex];
    if (firstDetail && isQuantityValue(firstDetail.value)) {
      quantityOrdered = parseAmount(firstDetail.value);
      detailIndex++;
    }
    const secondDetail = detailTokens[detailIndex];
    if (secondDetail && isQuantityValue(secondDetail.value) && secondDetail.x < 380) {
      quantityDelivered = parseAmount(secondDetail.value);
      detailIndex++;
    }

    let priceReferenceToken = detailTokens[detailIndex];
    let unitReference = null;
    if (priceReferenceToken && isQuantityValue(priceReferenceToken.value)) {
      unitReference = parseAmount(priceReferenceToken.value);
      detailIndex++;
    }

    let discountPercent = null;
    const discountToken = detailTokens[detailIndex];
    if (discountToken && discountToken.value.includes('%')) {
      discountPercent = parseAmount(discountToken.value);
      detailIndex++;
    }

    let unitPrice = null;
    const unitToken = detailTokens[detailIndex];
    if (unitToken && isQuantityValue(unitToken.value)) {
      unitPrice = parseAmount(unitToken.value);
      detailIndex++;
    }

    let lineTotal = null;
    const totalToken = detailTokens[detailIndex];
    if (totalToken && isQuantityValue(totalToken.value)) {
      lineTotal = parseAmount(totalToken.value);
      detailIndex++;
    }

    let vatRate = null;
    const vatToken = detailTokens[detailIndex];
    if (vatToken && isQuantityValue(vatToken.value)) {
      vatRate = parseAmount(vatToken.value);
    }

    if (quantityOrdered == null || unitPrice == null || lineTotal == null) {
      if (opts.debug) {
        console.debug('[MEGA DEBUG] Skip row (missing numeric fields)', reference, {
          quantityOrdered,
          unitReference,
          discountPercent,
          unitPrice,
          lineTotal,
          vatRate
        });
      }
        continue;
      }

    if (!currentKey) {
      const { key } = ensureHeader({ invoiceNumber: null, fallbackKey: `item-${pageNum ?? 0}-${++autoCounter}` });
      currentKey = key;
    }

    assignMetadata(currentKey, header => {
      if (pageNum != null) header.pages.add(pageNum);
    });

    const item = {
      reference,
      name: rawName,
      quantity: Number(quantityOrdered),
      quantity_delivered: quantityDelivered != null ? Number(quantityDelivered) : null,
      unit_price_ttc: Number(unitPrice),
      unit_price_ttc_reference: unitReference != null ? Number(unitReference) : null,
      discount_percent: discountPercent != null ? Number(discountPercent) : null,
      vat_percent: vatRate != null ? Number(vatRate) : null,
      line_total_ttc: Number(lineTotal),
      source_line: Number(yKey),
      source_page: pageTokens[0]?.page ?? null,
      __invoice_key: currentKey
    };

    if (opts.debug) {
      console.debug('[MEGA DEBUG] Row', yKey, item);
    }

    items.push(item);
  }

  const headersWithArrays = new Map();
  headers.forEach((header, key) => {
    headersWithArrays.set(key, {
      invoice_number: header.invoice_number,
      client_number: header.client_number,
      order_number: header.order_number,
      invoice_date: header.invoice_date,
      total_declared: header.total_declared,
      pages: Array.from(header.pages)
    });
  });

  return { items, headers: headersWithArrays };
}

function sumLineTotals(items) {
  return Math.round(items.reduce((acc, item) => acc + (item.line_total_ttc ?? (item.unit_price_ttc ?? 0) * (item.quantity ?? 0)), 0) * 100) / 100;
}

function extractMegaDental(text, opts = {}, tokens = [], linesByPage = {}) {
  const tokensByPage = tokens.reduce((acc, token) => {
    if (!acc[token.page]) acc[token.page] = [];
    acc[token.page].push(token);
    return acc;
  }, {});

  const pageNumbers = Object.keys(linesByPage).map(Number).sort((a, b) => a - b);
  const invoicesMap = new Map();

  for (const pageNum of pageNumbers) {
    const lines = linesByPage[pageNum] || [];
    if (!lines.length) continue;

    const pageText = lines.join('\n');
    const upper = pageText.toUpperCase();
    if (!upper.includes('MEGA')) continue;
    if (upper.includes('BON DE COLISAGE') || upper.includes('INFORMATION POUR RETOUR')) continue;

    const pageTokens = tokensByPage[pageNum] || [];
    const { items, headers: pageHeaders } = extractMegaDentalItemsFromTokens(pageTokens, opts, pageNum);
    if (items.length === 0 && pageHeaders.size === 0) continue;

    pageHeaders.forEach((header, key) => {
      if (!invoicesMap.has(key)) {
        invoicesMap.set(key, {
          supplier: 'MEGADENTAL',
          invoice_number: header.invoice_number,
          client_number: header.client_number,
          order_number: header.order_number,
          invoice_date: header.invoice_date,
          total_declared: header.total_declared,
          computed_total: 0,
          items: [],
          warnings: [],
          pages: new Set(header.pages),
          raw_texts: opts.debug ? [] : undefined
        });
      } else {
        const bucket = invoicesMap.get(key);
        if (header.invoice_number && !bucket.invoice_number) bucket.invoice_number = header.invoice_number;
        if (header.client_number && !bucket.client_number) bucket.client_number = header.client_number;
        if (header.order_number && !bucket.order_number) bucket.order_number = header.order_number;
        if (header.invoice_date && !bucket.invoice_date) bucket.invoice_date = header.invoice_date;
        if (header.total_declared != null) bucket.total_declared = header.total_declared;
        header.pages.forEach(p => bucket.pages.add(p));
      }
    });

    const defaultKey = `auto-page-${pageNum}`;

    items.forEach(item => {
      const key = item.__invoice_key || defaultKey;
      if (!invoicesMap.has(key)) {
        invoicesMap.set(key, {
          supplier: 'MEGADENTAL',
          invoice_number: null,
          client_number: null,
          order_number: null,
          invoice_date: null,
          total_declared: null,
          computed_total: 0,
          items: [],
          warnings: [],
          pages: new Set([pageNum]),
          raw_texts: opts.debug ? [] : undefined
        });
      }

      const bucket = invoicesMap.get(key);
      const cleanItem = { ...item };
      delete cleanItem.__invoice_key;
      bucket.items.push(cleanItem);
      const lineValue = cleanItem.line_total_ttc != null
        ? cleanItem.line_total_ttc
        : Math.round(((cleanItem.unit_price_ttc ?? 0) * (cleanItem.quantity ?? 0)) * 100) / 100;
      bucket.computed_total = Math.round((bucket.computed_total + lineValue) * 100) / 100;
      if (pageNum != null) bucket.pages.add(pageNum);
    });

    if (opts.debug) {
      const targets = pageHeaders.size ? pageHeaders : new Map([[defaultKey, { pages: [pageNum] }]]);
      targets.forEach((_, key) => {
        const bucket = invoicesMap.get(key);
        if (bucket && bucket.raw_texts) bucket.raw_texts.push(pageText);
      });
    }
  }

  const invoices = Array.from(invoicesMap.values()).map(inv => {
    const declared = inv.total_declared;
    const computed = Math.round(inv.computed_total * 100) / 100;
    const total_ttc = declared != null ? declared : computed;

    if (declared != null && Math.abs(computed - declared) > 0.5) {
      inv.warnings.push(`Somme des lignes (${computed} €) différente du total TTC (${declared} €)`);
    }

    const pagesArray = Array.from(inv.pages || []).sort((a, b) => a - b);

    return {
      supplier: 'MEGADENTAL',
      invoice_number: inv.invoice_number,
      client_number: inv.client_number,
      order_number: inv.order_number,
      invoice_date: inv.invoice_date,
      total_ttc,
      items: inv.items,
      pages: pagesArray,
      warnings: Array.from(new Set(inv.warnings)),
      raw_text: opts.debug && inv.raw_texts ? inv.raw_texts.join('\n\n') : undefined
    };
  });

  if (invoices.length === 0) {
    throw new Error('Aucune facture Megadental détectée');
  }

  if (invoices.length === 1) {
    const invoice = invoices[0];
    return {
      supplier: 'MEGADENTAL',
      invoice_number: invoice.invoice_number,
      client_number: invoice.client_number,
      invoice_date: invoice.invoice_date,
      order_number: invoice.order_number,
      total_ttc: invoice.total_ttc,
      items: invoice.items,
      pages: invoice.pages,
      raw_text: opts.debug ? invoice.raw_text || null : null,
      warnings: invoice.warnings
    };
  }

  const globalWarnings = Array.from(new Set(invoices.flatMap(inv => inv.warnings)));

  return {
    supplier: 'MEGADENTAL',
    invoice_count: invoices.length,
    total_ttc: Math.round(invoices.reduce((acc, inv) => acc + (inv.total_ttc ?? 0), 0) * 100) / 100,
    invoices: invoices.map(inv => ({
      supplier: 'MEGADENTAL',
      invoice_number: inv.invoice_number,
      client_number: inv.client_number,
      invoice_date: inv.invoice_date,
      order_number: inv.order_number,
      total_ttc: inv.total_ttc,
      items: inv.items,
      pages: inv.pages,
      warnings: inv.warnings,
      raw_text: opts.debug ? inv.raw_text : undefined
    })),
    items: invoices.flatMap(inv => inv.items.map(item => ({
      ...item,
      invoice_number: inv.invoice_number,
      invoice_page: inv.pages?.[0] ?? null,
      invoice_pages: inv.pages
    }))),
    warnings: globalWarnings
  };
}

/**
 * Extraire données facture avec fallback OCR si nécessaire
 */
async function extractWithOCR(page, pdfjsLib) {
  try {
    if (typeof document === 'undefined') {
      throw new Error('OCR non supporté dans cet environnement');
    }

    const canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.getContext !== 'function') {
      throw new Error('Canvas API indisponible');
    }

    const Tesseract = (await import('tesseract.js')).default;
    const viewport = page.getViewport({ scale: 2 });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Contexte 2D indisponible');
    }
    
    await page.render({ canvasContext: ctx, viewport }).promise;
    
    const { data: { text } } = await Tesseract.recognize(canvas, 'fra+eng', {
      logger: m => {
        if (m.status === 'recognizing text') console.debug('[OCR]', m.progress);
      }
    });
    
    return text;
  } catch (err) {
    console.warn('[OCR] Failed:', err.message);
    return '';
  }
}

/**
 * Fonction principale d'extraction
 */
export async function extractInvoiceFromPDF(fileOrArrayBuffer, opts = {}) {
  const warnings = [];
  
  try {
    // Config pdfjs
    let pdfjsLib;
    if (typeof window === 'undefined') {
      pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const basePath = `${process.cwd()}/node_modules/pdfjs-dist/legacy`;
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${basePath}/build/pdf.worker.mjs`;
      pdfjsLib.GlobalWorkerOptions.standardFontDataUrl = `${basePath}/standard_fonts/`;
    } else {
      pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    }
    
    // Get ArrayBuffer
    const arrayBuffer = fileOrArrayBuffer instanceof File 
      ? await fileOrArrayBuffer.arrayBuffer() 
      : fileOrArrayBuffer;
    
    // Load PDF
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    // Extract text per page - reconstruct lines properly from Y coordinates
    let fullText = '';
    const tokens = [];
    const linesByPage = {};
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      // Group tokens by Y position to reconstruct lines
      const tokensByY = {};
      for (const item of textContent.items) {
        const str = normalizeString(item.str);
        if (str && item.transform) {
          const y = item.transform[5];
          const yRounded = Math.round(y / 3) * 3; // Group by 3px tolerance
          
          if (!tokensByY[yRounded]) {
            tokensByY[yRounded] = [];
          }
          tokensByY[yRounded].push({
            s: str,
            x: item.transform[4],
            page: pageNum
          });
          
          tokens.push({
            s: str,
            x: item.transform[4],
            y: item.transform[5],
            page: pageNum
          });
        }
      }
      
      // Sort Y positions from top to bottom and build lines
      const sortedYs = Object.keys(tokensByY).sort((a, b) => parseFloat(b) - parseFloat(a));
      const pageLines = [];
      
      for (const y of sortedYs) {
        const lineTokens = tokensByY[y].sort((a, b) => a.x - b.x); // Sort by X
        const lineText = lineTokens.map(t => t.s).join(' ');
        if (lineText.trim().length > 0) {
          pageLines.push(lineText.trim());
        }
      }
      
      const pageText = pageLines.join('\n');
      fullText += pageText + '\n\n';
      
      linesByPage[pageNum] = pageLines;
      
      // OCR fallback if too little text
      if (!opts.disableOCR && pageText.length < 40 && pageNum === 1) {
        if (opts.debug) console.debug('[OCR] Low text, attempting OCR');
        const ocrText = await extractWithOCR(page, pdfjsLib);
        if (ocrText) {
          warnings.push('OCR utilisé (PDF scanné)');
          fullText += ' ' + ocrText + '\n';
        }
      }
    }
    
    if (opts.debug) {
      console.debug('[DEBUG] Extracted text length:', fullText.length);
      console.debug('[DEBUG] First 500 chars:', fullText.substring(0, 500));
    }

    if (!fullText || fullText.trim().length === 0) {
      throw new Error('PDF ne contient pas de texte extractible');
    }
    
    // Detect supplier
    const supplier = detectSupplier(fullText, pdf.numPages);
    
    if (!supplier) {
      throw new Error('Fournisseur non reconnu');
    }
    
    // Extract based on supplier
    let result;
    switch (supplier) {
      case 'GACD':
        result = extractGACD(fullText, opts, tokens);
        break;
      case 'MEGADENTAL':
        result = extractMegaDental(fullText, opts, tokens, linesByPage);
        break;
      case 'HENRY-SCHEIN':
        throw new Error('Parser Henry Schein non encore implémenté');
      default:
        throw new Error(`Fournisseur non supporté: ${supplier}`);
    }
    
    // Add global warnings
    if (warnings.length > 0) {
      result.warnings = [...(result.warnings || []), ...warnings];
    }
    
    if (opts.debug) {
      console.debug('[EXTRACT] Result:', result);
    }

    return result;
    
  } catch (error) {
    throw new Error(`Erreur extraction facture: ${error.message}`);
  }
}
